import { useState, useEffect, useRef, useCallback } from "react";
import throttle from "lodash/throttle";

// Hook
export function useOnScreen(ref, rootMargin = "0px") {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting);
      },
      {
        rootMargin,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount
  return isIntersecting;
}

export function useVisibility(offset = 0, throttleMilliseconds = 100) {
  const [isVisible, setIsVisible] = useState(false);
  const currentElement = useRef(null);

  const onScroll = useCallback(
    throttle(() => {
      if (!currentElement.current) {
        setIsVisible(false);
        return;
      }
      const top = currentElement.current.getBoundingClientRect().top;
      setIsVisible(top + offset >= 0 && top - offset <= window.innerHeight);
    }, throttleMilliseconds),
    []
  );

  useEffect(() => {
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, []);

  useEffect(() => {
    function handleResize() {
      const top = currentElement.current.getBoundingClientRect().top;
      setIsVisible(top + offset >= 0 && top - offset <= window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return {
    reset: () => setIsVisible(false),
    isVisible,
    marker: currentElement,
    update: () => {
      const top = currentElement.current.getBoundingClientRect().top;
      setIsVisible(top + offset >= 0 && top - offset <= window.innerHeight);
    },
  };
}
